// src/LoginPage.js
import React, { useState, useEffect } from "react";
import { fetchUsers, saveUser, sendEmail } from "./service/APIService";
import { FaFingerprint, FaEnvelope, FaUser, FaShieldAlt, FaArrowRight, FaTimes } from "react-icons/fa";
import secureLocalStorage from "react-secure-storage";
import logo from "./images/recipeailogo.jpg";
import { saveStoredProfilePic } from "./utils/sessionUtils";
import config from "./config.json";
import WelcomeOnboarding from "./pages7-recipie-ai/WelcomeOnboarding";
import "./styles/LoginPage.css";

const SENDER_EMAIL = "Recipe AI <support@theuniquecreations.com>";

const LoginPage = ({ onLogin }) => {
  const getValidSavedEmail = () => {
    try {
      const remembered =
        secureLocalStorage.getItem("rememberedEmail") ||
        (typeof localStorage !== "undefined" && localStorage.getItem("rememberedEmail"));
      if (remembered && typeof remembered === "string" && remembered.includes("@") && remembered !== "guest") {
        return remembered;
      }
      const logged =
        secureLocalStorage.getItem("loggedInUserEmail") ||
        (typeof localStorage !== "undefined" && localStorage.getItem("loggedInUserEmail"));
      if (logged && typeof logged === "string" && logged.includes("@") && logged !== "guest") {
        return logged;
      }
      const rawUser =
        secureLocalStorage.getItem("loggedInUser") ||
        (typeof localStorage !== "undefined" && localStorage.getItem("loggedInUser"));
      if (rawUser) {
        const parsed = typeof rawUser === "string" ? JSON.parse(rawUser) : rawUser;
        if (parsed?.email && typeof parsed.email === "string" && parsed.email.includes("@") && parsed.email !== "guest") {
          return parsed.email;
        }
      }
    } catch {
      // ignore
    }
    return "";
  };

  const [activeTab, setActiveTab] = useState("signin"); // "signin" | "register"
  const [email, setEmail] = useState(() => getValidSavedEmail());
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Biometric state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState("FaceID");

  // Show Welcome Onboarding for first-time / non-logged in users until they click Continue
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      const seen =
        secureLocalStorage.getItem("hasSeenWelcomeScreen") === "true" ||
        (typeof localStorage !== "undefined" && localStorage.getItem("hasSeenWelcomeScreen") === "true");
      return !seen;
    } catch {
      return true;
    }
  });

  const handleDismissWelcome = () => {
    try {
      secureLocalStorage.setItem("hasSeenWelcomeScreen", "true");
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("hasSeenWelcomeScreen", "true");
      }
    } catch {}
    setShowWelcome(false);
  };

  const savedEmail = getValidSavedEmail();
  const hasLoggedInOnce =
    secureLocalStorage.getItem("hasLoggedInOnce") === "true" ||
    (typeof localStorage !== "undefined" && localStorage.getItem("hasLoggedInOnce") === "true");
  const isBiometricExplicitlyDisabled = secureLocalStorage.getItem("biometricEnabled") === "false";
  const canShowFaceId = Boolean(
    hasLoggedInOnce &&
    !isBiometricExplicitlyDisabled &&
    savedEmail &&
    (typeof window !== "undefined" && (!!window.ReactNativeWebView || biometricAvailable))
  );

  useEffect(() => {
    const remembered = getValidSavedEmail();
    if (remembered) {
      setEmail((prev) => prev || remembered);
    }
  }, []);

  // Listen for native biometric responses from React Native WebView (TUCWebViewApp)
  useEffect(() => {
    const handleNativeMessage = async (event) => {
      try {
        let data = event.data;
        if (typeof data === "string") {
          data = JSON.parse(data);
        }
        if (!data || !data.type) return;

        if (data.type === "BIOMETRIC_CHECK_RESULT") {
          setBiometricAvailable(!!data.available);
          if (data.biometryType) {
            setBiometryType(data.biometryType);
          }
        } else if (data.type === "BIOMETRIC_VERIFY_RESULT") {
          if (data.success) {
            const targetEmail = getValidSavedEmail();
            if (targetEmail) {
              setLoading(true);
              let user = await fetchUsers(targetEmail);
              if (!user) {
                user = {
                  email: targetEmail,
                  name: targetEmail.split("@")[0],
                  country: "us",
                };
                await saveUser(user);
              }
              secureLocalStorage.setItem("rememberedEmail", user.email);
              secureLocalStorage.setItem("loggedInUserEmail", user.email);
              secureLocalStorage.setItem("loggedInUser", JSON.stringify(user));
              secureLocalStorage.setItem("biometricEnabled", "true");
              secureLocalStorage.setItem("hasLoggedInOnce", "true");
              secureLocalStorage.setItem("isLoggedOut", false);

              if (typeof localStorage !== "undefined") {
                localStorage.setItem("rememberedEmail", user.email);
                localStorage.setItem("loggedInUserEmail", user.email);
                localStorage.setItem("loggedInUser", JSON.stringify(user));
                localStorage.setItem("isLoggedOut", "false");
              }

              if (user.profilePic !== undefined) {
                saveStoredProfilePic(user.profilePic || "", user.email);
              }

              onLogin({
                email: user.email,
                name: user.name || user.email.split("@")[0],
                country: user.country || "",
              });
              setLoading(false);
            }
          } else if (data.error && !data.error.toLowerCase().includes("cancel")) {
            setError(data.error);
          }
        }
      } catch {
        // ignore
      }
    };

    window.addEventListener("message", handleNativeMessage);
    document.addEventListener("message", handleNativeMessage);

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "BIOMETRIC_CHECK" }));
    }

    return () => {
      window.removeEventListener("message", handleNativeMessage);
      document.removeEventListener("message", handleNativeMessage);
    };
  }, [onLogin]);

  const handleBiometricLogin = () => {
    setError("");
    const targetEmail = getValidSavedEmail();
    if (!targetEmail) {
      setError("Please sign in with email verification first to enable biometric login.");
      return;
    }
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "BIOMETRIC_VERIFY",
          reason: `Sign in to Recipe AI as ${targetEmail}`,
        })
      );
    } else {
      // Web fallback: simulate verified session
      handleFastLogin(targetEmail);
    }
  };

  const handleFastLogin = async (targetEmail) => {
    setLoading(true);
    try {
      let user = await fetchUsers(targetEmail);
      if (!user) {
        user = { email: targetEmail, name: targetEmail.split("@")[0] };
        await saveUser(user);
      }
      secureLocalStorage.setItem("rememberedEmail", user.email);
      secureLocalStorage.setItem("loggedInUserEmail", user.email);
      secureLocalStorage.setItem("loggedInUser", JSON.stringify(user));
      secureLocalStorage.setItem("hasLoggedInOnce", "true");
      secureLocalStorage.setItem("isLoggedOut", false);

      if (typeof localStorage !== "undefined") {
        localStorage.setItem("rememberedEmail", user.email);
        localStorage.setItem("loggedInUserEmail", user.email);
        localStorage.setItem("loggedInUser", JSON.stringify(user));
        localStorage.setItem("isLoggedOut", "false");
      }

      if (user.profilePic !== undefined) {
        saveStoredProfilePic(user.profilePic || "", user.email);
      }

      onLogin(user);
    } catch (err) {
      setError("Could not complete sign in: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateOtp = () => Math.floor(100000 + Math.random() * 900000).toString();

  // Send OTP
  const handleSendOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const cleanEmail = (email || "").trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    const isDemoAppleUser = cleanEmail === "demo@apple.com";
    if (activeTab === "register" && !name.trim() && !isDemoAppleUser) {
      setError("Please enter your name to register.");
      return;
    }

    setSendingOtp(true);
    setError("");
    setSuccessMsg("");

    // Apple App Review & Demo Account bypass
    if (isDemoAppleUser) {
      const otpCode = "123456";
      setGeneratedOtp(otpCode);
      try {
        secureLocalStorage.setItem("rememberedEmail", cleanEmail);
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("rememberedEmail", cleanEmail);
        }
      } catch (err) {}
      setIsOtpSent(true);
      setSuccessMsg(`Verification code sent to ${cleanEmail}`);
      setSendingOtp(false);
      return;
    }

    try {
      const otpCode = generateOtp();
      setGeneratedOtp(otpCode);
      const requestDateTime = new Date().toLocaleString();

      const htmlContent = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 460px; margin: 0 auto; padding: 28px; border-radius: 18px; background: #0f1f14; color: #F8FAFC; border: 1px solid rgba(34,197,94,0.3);">
          <div style="text-align: center; margin-bottom: 20px;">
            <h1 style="color: #22c55e; margin: 0; font-size: 22px; font-weight: 700;">Recipe AI</h1>
            <p style="color: #94A3B8; font-size: 13px; margin: 4px 0 0 0;">AI Cooking & Nutrition Assistant</p>
          </div>
          <p style="color: #E2E8F0; font-size: 14px; margin-bottom: 16px;">
            Hello ${name.trim() || cleanEmail.split("@")[0]},
          </p>
          <p style="color: #94A3B8; font-size: 14px; margin-bottom: 24px;">
            Your one-time verification code to ${activeTab === "register" ? "register your account" : "sign in"} is:
          </p>
          <div style="background: #182e1f; padding: 18px; border-radius: 12px; text-align: center; margin-bottom: 24px; border: 1px solid rgba(34,197,94,0.4);">
            <span style="font-size: 34px; font-weight: 800; letter-spacing: 8px; color: #22c55e;">${otpCode}</span>
          </div>
          <p style="color: #94a3b8; font-size: 12px; text-align: center; margin: 0;">
            Requested on ${requestDateTime}. Never share this code with anyone.
          </p>
        </div>
      `;

      const emailData = {
        sender: SENDER_EMAIL,
        recipient: cleanEmail,
        subject: `Recipe AI - ${otpCode} is your verification code`,
        cc: cleanEmail,
        body: htmlContent,
      };

      const emailResult = await sendEmail(emailData);
      if (emailResult && emailResult.error) {
        throw new Error(emailResult.error);
      }
      try {
        secureLocalStorage.setItem("rememberedEmail", cleanEmail);
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("rememberedEmail", cleanEmail);
        }
      } catch (e) {}
      setIsOtpSent(true);
      setSuccessMsg(`Verification code sent to ${cleanEmail}`);
    } catch (err) {
      console.error("Error sending OTP:", err);
      setError("Failed to send verification email. Please check your connection.");
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP and complete login / register
  const handleVerifyOtp = async (e, otpValue) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;

    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanOtp = (typeof otpValue === "string" ? otpValue : otp || "").trim();

    if (!cleanOtp) {
      setError("Please enter the 6-digit code.");
      return;
    }

    const isDemoAppleUser = cleanEmail === "demo@apple.com";
    const isOtpValid = cleanOtp === generatedOtp.trim() || (isDemoAppleUser && cleanOtp === "123456");

    if (!isOtpValid) {
      setError("Invalid code. Please check your email or click Resend.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let storedUser = null;
      try {
        storedUser = await fetchUsers(cleanEmail);
      } catch (err) {
        console.warn("fetchUsers check error:", err);
      }

      if (!storedUser) {
        storedUser = {
          email: cleanEmail,
          name: name.trim() || (isDemoAppleUser ? "Apple Reviewer" : cleanEmail.split("@")[0]),
          country: "us",
        };
        try {
          await saveUser(storedUser);
        } catch (saveErr) {
          console.warn("saveUser warning:", saveErr);
        }
      } else if (name.trim() && !storedUser.name) {
        storedUser.name = name.trim();
      }

      secureLocalStorage.setItem("rememberedEmail", storedUser.email);
      secureLocalStorage.setItem("loggedInUserEmail", storedUser.email);
      secureLocalStorage.setItem("loggedInUser", JSON.stringify(storedUser));
      secureLocalStorage.setItem("biometricEnabled", "true");
      secureLocalStorage.setItem("hasLoggedInOnce", "true");
      secureLocalStorage.setItem("isLoggedOut", false);

      if (typeof localStorage !== "undefined") {
        localStorage.setItem("rememberedEmail", storedUser.email);
        localStorage.setItem("loggedInUserEmail", storedUser.email);
        localStorage.setItem("loggedInUser", JSON.stringify(storedUser));
        localStorage.setItem("isLoggedOut", "false");
      }

      if (storedUser.profilePic !== undefined) {
        saveStoredProfilePic(storedUser.profilePic || "", storedUser.email);
      }

      onLogin({
        email: storedUser.email,
        name: storedUser.name || storedUser.email.split("@")[0],
        country: storedUser.country || "",
      });
    } catch (err) {
      console.error("Verification login error:", err);
      setError("Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(val);
    if (val.length === 6 && !loading) {
      handleVerifyOtp(null, val);
    }
  };

  const handleExploreApp = () => {
    const guestData = {
      email: "guest",
      name: "Guest User",
      country: "us",
    };
    secureLocalStorage.setItem("guestUser", JSON.stringify(guestData));
    secureLocalStorage.setItem("loggedInUserEmail", "guest");
    secureLocalStorage.setItem("isLoggedOut", false);
    secureLocalStorage.removeItem("loggedInUser");
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("guestUser", JSON.stringify(guestData));
      localStorage.setItem("loggedInUserEmail", "guest");
      localStorage.setItem("isLoggedOut", "false");
      localStorage.removeItem("loggedInUser");
    }
    onLogin(guestData);
  };

  if (showWelcome) {
    return (
      <WelcomeOnboarding
        onContinue={handleDismissWelcome}
        onExplore={() => {
          handleDismissWelcome();
          handleExploreApp();
        }}
      />
    );
  }

  return (
    <div className="login-screen-wrapper px-3 py-3">
      {/* Brand Header */}
      <div className="text-center mb-3">
        <div className="d-inline-flex p-2 rounded-circle mb-2 login-logo-glow">
          <img src={logo} alt="Recipe AI" width="60" height="60" className="rounded-circle shadow-sm" />
        </div>
        <h3 className="fw-bold mb-1 text-dark" style={{ fontSize: "1.5rem" }}>{config.apptitle || "Recipe AI"}</h3>
        <p className="text-secondary small mb-0" style={{ fontSize: "13px" }}>
          Generate delicious recipes from ingredients you have in your fridge
        </p>
        {process.env.REACT_APP_ENV === "QA" && (
          <span className="badge-pill-coral mt-1">Non-Prod (QA)</span>
        )}
      </div>

      {/* Main Glass Card */}
      <div className="app-card login-card p-4 mx-auto" style={{ maxWidth: "460px" }}>
        {/* Face ID / Biometric Quick Sign In */}
        {canShowFaceId && (
          <div className="mb-4 text-center">
            <button
              type="button"
              className="btn btn-dark-glass w-100 py-3 d-flex align-items-center justify-content-center gap-2 border-mint"
              onClick={handleBiometricLogin}
              disabled={loading}
            >
              <FaFingerprint size={22} className="text-success" />
              <span className="fw-semibold text-dark">Sign In with {biometryType === "FaceID" ? "Face ID" : "Biometrics"}</span>
            </button>
            <div className="d-flex align-items-center my-3">
              <hr className="flex-grow-1 border-secondary opacity-25" />
              <span className="px-2 text-secondary small" style={{ fontSize: "12px" }}>or continue with email</span>
              <hr className="flex-grow-1 border-secondary opacity-25" />
            </div>
          </div>
        )}

        {/* Tab Toggle: Sign In vs Register */}
        <div className="login-tab-switch d-flex p-1 rounded-pill mb-4 border">
          <button
            type="button"
            className={`btn btn-sm flex-fill rounded-pill fw-semibold py-2 login-switch-btn ${
              activeTab === "signin" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("signin");
              setError("");
              setIsOtpSent(false);
            }}
          >
            Sign In
          </button>
          <button
            type="button"
            className={`btn btn-sm flex-fill rounded-pill fw-semibold py-2 login-switch-btn ${
              activeTab === "register" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("register");
              setError("");
              setIsOtpSent(false);
            }}
          >
            Register
          </button>
        </div>

        {error && (
          <div className="alert alert-danger py-2 px-3 small rounded-3 mb-3 d-flex justify-content-between align-items-center">
            <span>{error}</span>
            <FaTimes role="button" onClick={() => setError("")} />
          </div>
        )}

        {successMsg && (
          <div className="alert alert-success py-2 px-3 small rounded-3 mb-3">
            {successMsg}
          </div>
        )}

        {/* OTP Flow */}
        {!isOtpSent ? (
          <form onSubmit={handleSendOtp}>
            {activeTab === "register" && (
              <div className="mb-3">
                <label className="small text-secondary mb-1 fw-semibold">Your Full Name</label>
                <div className="input-group-seamless">
                  <span className="input-group-text">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Johan Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={activeTab === "register"}
                  />
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="small text-secondary mb-1 fw-semibold">Email Address</label>
              <div className="input-group-seamless">
                <span className="input-group-text">
                  <FaEnvelope />
                </span>
                <input
                  type="email"
                  className="form-control"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEmail(val);
                    try {
                      if (val) {
                        secureLocalStorage.setItem("rememberedEmail", val);
                        if (typeof localStorage !== "undefined") {
                          localStorage.setItem("rememberedEmail", val);
                        }
                      }
                    } catch (err) {}
                  }}
                  required
                />
              </div>
              <small className="text-secondary mt-1 d-block" style={{ fontSize: "11px" }}>
                We'll email a 6-digit verification code. No passwords required.
              </small>
            </div>

            <button
              type="submit"
              className="btn btn-success w-100 py-3 rounded-pill fw-bold text-white shadow-sm"
              disabled={sendingOtp}
            >
              {sendingOtp ? "Sending code..." : <>Send Verification Code <FaArrowRight className="ms-1" /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp}>
            <div className="mb-3 text-center">
              <span className="text-secondary small">Code sent to: </span>
              <span className="text-success fw-semibold small">{email}</span>
            </div>

            <div className="mb-4">
              <label className="small text-secondary mb-1 fw-semibold">Enter 6-Digit Code</label>
              <div className="input-group-seamless">
                <span className="input-group-text">
                  <FaShieldAlt />
                </span>
                <input
                  type="text"
                  maxLength="6"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="form-control text-center fs-4 fw-bold letter-spacing-4 text-success"
                  placeholder="------"
                  value={otp}
                  onChange={handleOtpChange}
                  autoFocus
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-success w-100 py-3 mb-2 rounded-pill fw-bold text-white shadow-sm"
              disabled={loading || otp.length < 6}
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>

            <div className="d-flex justify-content-between mt-3">
              <button
                type="button"
                className="btn btn-link text-secondary p-0 small text-decoration-none"
                onClick={() => setIsOtpSent(false)}
              >
                Change Email
              </button>
              <button
                type="button"
                className="btn btn-link text-success p-0 small text-decoration-none fw-semibold"
                onClick={handleSendOtp}
                disabled={sendingOtp}
              >
                {sendingOtp ? "Resending..." : "Resend Code"}
              </button>
            </div>
          </form>
        )}

        {/* Explore App / Guest Mode & Welcome Screen Link */}
        <div className="mt-4 pt-3 border-top text-center">
          <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap">
            <button
              type="button"
              className="btn btn-link text-success text-decoration-none fw-semibold p-0"
              onClick={handleExploreApp}
              style={{ fontSize: "13.5px" }}
            >
              Explore App &rarr;
            </button>
            <span className="text-secondary opacity-50">•</span>
            <button
              type="button"
              className="btn btn-link text-secondary text-decoration-none small p-0 welcome-preview-link"
              onClick={() => setShowWelcome(true)}
              style={{ fontSize: "13px" }}
            >
              ✨ View Welcome Tour
            </button>
          </div>
          <p className="text-secondary small mt-1 mb-0" style={{ fontSize: "12px" }}>
            Explore recipes and calories without an account
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
